import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ClipboardCheck, Wrench } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// ── Phase 1: print a hand-markable tool pick sheet ──────────────────
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '');
const toolRows = (groups, freeItems) => {
  const main = (groups || []).map((g) => {
    const items = g.items.map((it) => `
        <tr>
          <td class="tool">${esc(it.label)}</td>
          <td class="req">${esc(it.qty || 1)}</td>
          <td class="pick"></td>
        </tr>`).join('');
    return `<tr class="cat"><td colspan="3">${esc(g.category)}</td></tr>${items}`;
  }).join('');
  const free = (freeItems || []).length ? `<tr class="cat"><td colspan="3">As written on the request</td></tr>` + freeItems.map((p) => `
        <tr><td class="tool">${esc(p.name)}</td><td class="req">${esc(Number.isInteger(p.qty) ? p.qty : p.qty)}</td><td class="pick"></td></tr>`).join('') : '';
  return main + free;
};

// amendmentId: print just that day's add-on list (a fresh page for the crib); otherwise the original + every amendment underneath
export const printToolSheet = async (requestId, amendmentId = null) => {
  try {
    const { data } = await axios.get(`${API}/dispatch/requests/${requestId}/tool-sheet${amendmentId ? `?amendment_id=${amendmentId}` : ''}`, authHeader());
    const dateLine = data.end_date && data.end_date !== data.start_date
      ? `${data.start_date} to ${data.end_date}` : (data.start_date || '');
    const only = data.amendment_only;
    const rows = toolRows(data.tool_groups, only ? only.free_items : data.free_items);
    const amendments = (data.amendments || []).map((a) => {
      const groups = {};
      (a.tools || []).forEach((t) => { (groups[t.category || 'Other'] = groups[t.category || 'Other'] || []).push(t); });
      const r = toolRows(Object.entries(groups).map(([category, items]) => ({ category, items })), a.free_items);
      return `
      <h3 class="amend">+ Tool list amendment — ${esc(fmtDay(a.date))} <span>added by ${esc(a.created_by_name || '')}${a.note ? ` · ${esc(a.note)}` : ''}</span></h3>
      <table>
        <thead><tr><th>Tool / Item</th><th style="text-align:center;">Required</th><th>Picked (write qty)</th></tr></thead>
        <tbody>${r}</tbody>
      </table>`;
    }).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Tool Sheet ${esc(data.request_number)}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
      .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #1e3a5f; padding-bottom: 10px; }
      .title { font-size: 22px; font-weight: 800; color: #1e3a5f; }
      .code { font-size: 15px; font-weight: 700; margin-top: 2px; }
      .qr { text-align: right; }
      .qr img { width: 96px; height: 96px; }
      .qr .cap { font-size: 10px; color: #666; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin: 14px 0; font-size: 13px; }
      .meta b { color: #1e3a5f; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      th { background: #1e3a5f; color: #fff; font-size: 12px; text-align: left; padding: 6px 8px; }
      td { border: 1px solid #bbb; padding: 7px 8px; font-size: 13px; }
      td.req { text-align: center; width: 80px; font-weight: 700; }
      td.pick { width: 110px; background: #fafafa; }
      tr.cat td { background: #eef3f9; font-weight: 700; color: #1e3a5f; font-size: 12px; text-transform: uppercase; }
      .sign { margin-top: 22px; display: flex; gap: 40px; font-size: 13px; }
      .sign div { flex: 1; border-top: 1px solid #333; padding-top: 4px; }
      .note { margin-top: 10px; font-size: 11px; color: #666; }
      h3.amend { margin: 22px 0 4px; font-size: 14px; color: #92400e; border-top: 2px dashed #f59e0b; padding-top: 10px; }
      h3.amend span { font-weight: 400; font-size: 11px; color: #666; margin-left: 8px; }
      .amend-badge { display: inline-block; background: #fef3c7; color: #92400e; border: 1px solid #f59e0b; border-radius: 4px; padding: 2px 8px; font-size: 12px; font-weight: 700; margin-top: 4px; }
      @media print { body { margin: 12mm; } }
    </style></head><body>
      <div class="head">
        <div>
          <div class="title">🔧 Tool Pick Sheet${only ? ' — Amendment' : ''}</div>
          <div class="code">${esc(data.job_code)}</div>
          <div style="font-size:13px;margin-top:2px;">${esc(data.customer_name || '')}</div>
          ${only ? `<div class="amend-badge">Tool list for ${esc(fmtDay(only.date))}${only.note ? ` · ${esc(only.note)}` : ''} · added by ${esc(only.created_by_name || '')}</div>` : ''}
        </div>
        <div class="qr"><img src="${data.qr}" alt="qr"/><div class="cap">${esc(data.request_number)}</div></div>
      </div>
      <div class="meta">
        <div><b>Job Site:</b> ${esc(data.job_site || '—')}</div>
        <div><b>Date:</b> ${esc(dateLine)} · ${esc(data.start_time || '')}–${esc(data.end_time || '')}</div>
        <div><b>Work Order:</b> ${esc(data.work_order_number || '—')}</div>
        <div><b>Crew:</b> ${esc((data.crew || []).join(', ') || '—')}</div>
      </div>
      <table>
        <thead><tr><th>Tool / Item</th><th style="text-align:center;">Required</th><th>Picked (write qty)</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="3" style="text-align:center;color:#999;">No tools selected for this job.</td></tr>'}</tbody>
      </table>
      ${amendments}
      <div class="sign">
        <div>Picked / loaded by (print name)</div>
        <div>Signature</div>
        <div>Date</div>
      </div>
      <div class="note">Write the quantity picked for each item in the "Picked" column. Scan this sheet back to ops when complete — the QR code links it to job ${esc(data.request_number)}.</div>
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
    </body></html>`;

    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups to print the tool sheet'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Failed to build tool sheet');
  }
};

// ── Phase 2: digital "loaded tools" logging modal ───────────────────
export const LoadedToolsModal = ({ req, onClose, onSaved }) => {
  const required = req.tools || [];
  const existing = useMemo(() => {
    const m = {};
    (req.loaded_tools || []).forEach((l) => { m[l.id] = l.qty_picked; });
    return m;
  }, [req]);
  const [picked, setPicked] = useState(() => {
    const init = {};
    required.forEach((t) => { init[t.id] = existing[t.id] != null ? existing[t.id] : (t.qty || 1); });
    return init;
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/dispatch/requests/${req.id}/loaded-tools`, {
        picked: required.map((t) => ({ id: t.id, qty_picked: picked[t.id] })),
        source: 'manual',
      }, authHeader());
      toast.success('Loaded tools logged to the job');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log loaded tools');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="loaded-tools-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="bg-[#1e3a5f] p-2 rounded-lg"><ClipboardCheck className="text-white" size={18} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Log loaded tools</h2>
              <p className="text-xs text-gray-500">{req.request_number} — record what the crib actually picked</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700" data-testid="loaded-tools-close"><X size={20} /></button>
        </div>
        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {required.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No tools were selected on this request.</p>
          ) : (
            <div className="space-y-1.5">
              <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-xs font-semibold text-[#1e3a5f] uppercase tracking-wide px-1 pb-1">
                <span>Tool</span><span className="text-center w-16">Req</span><span className="text-center w-20">Picked</span>
              </div>
              {required.map((t) => {
                const p = Number(picked[t.id] ?? 0);
                const reqd = t.qty || 1;
                const short = p < reqd, over = p > reqd;
                return (
                  <div key={t.id} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-1 py-1" data-testid={`loaded-row-${t.id}`}>
                    <span className="text-sm text-gray-700 truncate">{t.label}</span>
                    <span className="text-sm text-gray-500 text-center w-16">{reqd}</span>
                    <input
                      type="number" min="0"
                      value={picked[t.id]}
                      onChange={(e) => setPicked((prev) => ({ ...prev, [t.id]: e.target.value }))}
                      className={`w-20 text-center border rounded-md px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#1e3a5f] ${short ? 'border-amber-400 bg-amber-50' : over ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`}
                      data-testid={`loaded-qty-${t.id}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={busy} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-60">Cancel</button>
          <button onClick={save} disabled={busy || required.length === 0} className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#16304f] text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60" data-testid="loaded-tools-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Wrench size={16} />}Save loaded log
          </button>
        </div>
      </div>
    </div>
  );
};
